import { createSlice } from "@reduxjs/toolkit";
import { resolveApiMessage } from "../../helpers/apiMessage";

import { createZone, deleteZone, getZone, getZones, updateZone } from "./thunk";

export interface ZonesState {
    zones: any[];
    loading: boolean;
    submitting: boolean;
    error: string | null;
}

export const initialState: ZonesState = {
    zones: [],
    loading: false,
    submitting: false,
    error: null,
};

const extractPayloadData = (payload: any): any => {
    if (!payload) {
        return undefined;
    }

    if (typeof payload === "object" && "data" in payload) {
        return (payload as Record<string, any>).data;
    }

    return payload;
};

const extractResponsePayload = (payload: any): any => {
    if (payload && typeof payload === "object" && "response" in payload) {
        return extractPayloadData((payload as Record<string, any>).response);
    }

    return extractPayloadData(payload);
};

const parseZoneList = (payload: any): any[] => {
    const data = extractResponsePayload(payload);

    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray((data as any)?.items)) {
        return (data as any).items;
    }

    if (Array.isArray((data as any)?.zones)) {
        return (data as any).zones;
    }

    if (Array.isArray((data as any)?.data)) {
        return (data as any).data;
    }

    return [];
};

const parseZoneItem = (payload: any): any | null => {
    const data = extractResponsePayload(payload);

    if (!data) {
        return null;
    }

    if (Array.isArray(data)) {
        return data[0] ?? null;
    }

    if (data && typeof data === "object") {
        if ((data as any).zone && typeof (data as any).zone === "object") {
            return (data as any).zone;
        }

        if ((data as any).data && typeof (data as any).data === "object" && !Array.isArray((data as any).data)) {
            return (data as any).data;
        }
    }

    return data;
};

const getZoneIdValue = (zone: any): string | number | undefined => {
    if (!zone || typeof zone !== "object") {
        return undefined;
    }

    const candidateKeys = ["id", "zoneId", "zone_id", "uuid", "_id", "slug", "code"];

    for (const key of candidateKeys) {
        const value = (zone as Record<string, any>)[key];
        if (value !== undefined && value !== null && value !== "") {
            return value as string | number;
        }
    }

    return undefined;
};

const toComparableId = (value: string | number | undefined | null): string | undefined => {
    if (value === undefined || value === null) {
        return undefined;
    }

    return String(value);
};

const upsertZone = (zones: any[], zone: any, fallbackId?: string | number) => {
    if (!Array.isArray(zones)) {
        return;
    }

    const identifier = getZoneIdValue(zone) ?? fallbackId;
    const comparableIdentifier = toComparableId(identifier ?? null);

    if (!comparableIdentifier) {
        zones.push(zone);
        return;
    }

    const index = zones.findIndex(
        (item) => toComparableId(getZoneIdValue(item) ?? null) === comparableIdentifier
    );

    if (index >= 0) {
        zones[index] = {
            ...zones[index],
            ...zone,
        };
        return;
    }

    zones.push(
        getZoneIdValue(zone) === undefined
            ? {
                  ...zone,
                  id: identifier,
              }
            : zone
    );
};

const removeZoneById = (zones: any[], zoneId: string | number): any[] => {
    const comparableIdentifier = toComparableId(zoneId);

    if (!comparableIdentifier) {
        return zones;
    }

    return zones.filter((item) => toComparableId(getZoneIdValue(item) ?? null) !== comparableIdentifier);
};

const getErrorMessage = (payload: any): string => {
    if (!payload) {
        return resolveApiMessage(payload);
    }

    if (typeof payload === "string") {
        return payload;
    }

    if (payload && typeof payload === "object") {
        if (typeof (payload as any).message === "string") {
            return (payload as any).message as string;
        }

        if (typeof (payload as any).error === "string") {
            return (payload as any).error as string;
        }
    }

    return resolveApiMessage(payload);
};

const zonesSlice = createSlice({
    name: "Zones",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getZones.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getZones.fulfilled, (state, action) => {
                state.loading = false;
                state.zones = parseZoneList(action.payload);
            })
            .addCase(getZones.rejected, (state, action) => {
                state.loading = false;
                state.zones = [];
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(getZone.fulfilled, (state, action) => {
                const payload = action.payload as any;
                const zone = parseZoneItem(payload?.response ?? payload);
                const fallbackId = payload?.zoneId;
                if (zone) {
                    upsertZone(state.zones, zone, fallbackId);
                }
            })
            .addCase(createZone.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(createZone.fulfilled, (state, action) => {
                state.submitting = false;
                const zone = parseZoneItem(action.payload);
                if (zone) {
                    upsertZone(state.zones, zone);
                }
            })
            .addCase(createZone.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(updateZone.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(updateZone.fulfilled, (state, action) => {
                state.submitting = false;
                const payload = action.payload as any;
                const zone = parseZoneItem(payload?.response ?? payload);
                const fallbackId = payload?.zoneId;
                if (zone) {
                    upsertZone(state.zones, zone, fallbackId);
                } else if (fallbackId) {
                    upsertZone(state.zones, { id: fallbackId, ...payload?.request }, fallbackId);
                }
            })
            .addCase(updateZone.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            })
            .addCase(deleteZone.pending, (state) => {
                state.submitting = true;
                state.error = null;
            })
            .addCase(deleteZone.fulfilled, (state, action) => {
                state.submitting = false;
                const zoneId = (action.payload as any)?.zoneId;
                if (zoneId !== undefined) {
                    state.zones = removeZoneById(state.zones, zoneId);
                }
            })
            .addCase(deleteZone.rejected, (state, action) => {
                state.submitting = false;
                const errorPayload = (action as any).payload ?? action.error;
                state.error = getErrorMessage(errorPayload);
            });
    },
});

export default zonesSlice.reducer;
